import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  afterNextRender
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormControl,
  FormGroup
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// PrimeNG modules
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule, CheckboxChangeEvent } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';

import { ToastrShowService } from 'src/app/core/services/toastr.service';
import { AuthUserService } from 'src/app/core/services/auth-user.service';
import { RoleId } from 'src/app/core/common-enums/enum';
import {
  ActionButton,
  BrokerAdminRmClient,
  RMData
} from 'src/app/core/models/broker-admin';
import { SharedModuleService } from 'src/app/core/services/shared-module.service';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { NormalizedData } from '../../../../core/utils/model';
import { NORMALISEDATA } from 'src/app/core/constants/login.constants';
import { LoginResponse } from 'src/app/core/models/login';
import { StaticTextService } from 'src/app/core/services/static-text.service';

import {
  CloseEventResult,
  DropdownOption,
  InviteSidebarData
} from './invite-update-sidebar.models';
import { InviteUpdateSidebarFormService } from './services/invite-update-sidebar-form.service';
import { InviteUpdateSidebarDataService } from './services/invite-update-sidebar-data.service';

@Component({
  selector: 'app-invite-update-sidebar',
  templateUrl: './invite-update-sidebar.component.html',
  styleUrls: ['./invite-update-sidebar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    // PrimeNG
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ButtonModule,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    TooltipModule,
    MultiSelectModule
  ]
})
export class InviteUpdateSidebarComponent {
  // Dependency injection
  private readonly destroyRef = inject(DestroyRef);
  public readonly staticTextService = inject(StaticTextService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly authUserService = inject(AuthUserService);
  private readonly toastr = inject(ToastrShowService);
  private readonly sharedService = inject(SharedModuleService);
  private readonly clientState = inject(ClientStateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly formService = inject(InviteUpdateSidebarFormService);
  private readonly dataService = inject(InviteUpdateSidebarDataService);

  // PrimeNG DynamicDialog support (when opened via DialogService)
  private readonly dynamicDialogRef = inject(DynamicDialogRef, {
    optional: true
  });
  private readonly dynamicDialogConfig = inject(DynamicDialogConfig, {
    optional: true
  });

  // Input setter — re-initialises the component whenever new modalData arrives
  private _data: InviteSidebarData | null = null;
  @Input()
  set data(value: InviteSidebarData | null) {
    this._data = value;
    if (!value?.modalData) {
      return;
    }
    this.getModalData();
    this.setupUserSubscription();
    this.initializeComponent();
    this.configureModalForm();
    this.loadInitialData();
    this.cdr.markForCheck();
  }
  get data(): InviteSidebarData | null {
    return this._data;
  }
  @Output() closed = new EventEmitter<CloseEventResult | null>();

  // ── Signals ──────────────────────────────────────────────────────────────
  rmOptions = signal<DropdownOption[]>([]);
  clientOptions = signal<DropdownOption[]>([]);
  filteredClientOptions = signal<DropdownOption[]>([]);
  filteredRmOptions = signal<DropdownOption[]>([]);
  dependantOptions = signal<DropdownOption[]>([]);
  loading = signal<boolean>(false);
  startsWithZero = signal<boolean>(false);
  showExistingRm = signal<boolean>(false);
  isMultipleTrue = signal<boolean>(false);
  hasRMboolean = signal<boolean>(false);
  dependantsChanged = signal(false);

  // ── Component state ───────────────────────────────────────────────────────
  modalForm!: FormGroup;
  userId: string = '';
  organizationId!: number;
  organizationName: string = '';
  modalData!: ActionButton;
  clientName: string = '';
  rmName: string = '';
  existingRmDetails!: RMData;
  role!: number;
  currentRoleId!: number;
  searchTermControl = new FormControl('');
  filteredData: NormalizedData[] = [];
  filteredDataDependant: NormalizedData[] = [];
  valueRm: string = '';
  userMemberRole: string = '';
  dependantIds: string[] = [];
  selectedDependants: string[] = [];
  private initialDependants: string[] = [];

  constructor() {
    this.modalForm = this.formBuilder.group({});

    afterNextRender(() => {
      // Priority 1: DynamicDialogConfig data (PrimeNG DialogService)
      if (this.dynamicDialogConfig?.data) {
        this.data = this.dynamicDialogConfig.data;
      }
      this.setupUserSubscription();
    });
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  private initializeComponent(): void {
    if (!this.modalData) {
      return;
    }

    if (this.modalData.actionButton === 'Update Dependants') {
      this.isMultipleTrue.set(true);
      this.loadDependants(this.modalData.clientUserId as string);
    }

    if (this.modalData.actionButton === 'Send Email Invitation') {
      this.hasRMboolean.set(false);
      this.role = RoleId.RelationshipManager;
    } else {
      this.hasRMboolean.set(true);
      this.setUserRole();
    }
  }

  private setUserRole(): void {
    const storedRoleDetails = localStorage.getItem('roleDetails');
    const userDetails = storedRoleDetails
      ? (JSON.parse(storedRoleDetails) as LoginResponse)
      : null;
    this.role =
      userDetails?.newRoleFinal?.roleId === RoleId.Advisor
        ? userDetails.newRoleFinal.roleId
        : RoleId.BrokerAdmin;
  }

  private setupUserSubscription(): void {
    const storedRoleDetails = localStorage.getItem('roleDetails');
    const userDetails = storedRoleDetails
      ? (JSON.parse(storedRoleDetails) as LoginResponse)
      : null;
    if (userDetails?.newRoleFinal?.roleId) {
      this.currentRoleId = userDetails.newRoleFinal.roleId;
    }

    this.authUserService
      .getUserDetailBehaviorSubject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.userId = data.userId;
          this.organizationId = data.organizationId;
          this.organizationName = data.organizationName;
          this.cdr.markForCheck();
        }
      });
  }

  private configureModalForm(): void {
    this.modalForm = this.formService.buildForm(this.modalData, {
      userId: this.userId,
      organizationId: this.organizationId,
      organizationName: this.organizationName,
      role: this.role,
      hasRMboolean: this.hasRMboolean(),
      currentRoleId: this.currentRoleId
    });
  }

  private loadInitialData(): void {
    const action = this.modalData.actionButton;
    if (
      action === 'Assign RM' ||
      action === 'Reassign RM or Broker' ||
      action === 'Reassign RM or Advisor'
    ) {
      this.getBrokerAdminRmList();
    } else if (
      action === 'Assign Client' ||
      action === 'Reassign Client' ||
      action === 'Update Dependants'
    ) {
      this.getBrokerAdminClientList();
    }
  }

  private loadDependants(clientId: string): void {
    this.dataService
      .getDependants(clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deps) => {
        this.dependantIds = deps.map((d) => d.clientUserId);
        this.clientState.setSelectedClientIds(this.dependantIds);
        this.selectedDependants = [...this.dependantIds];
        this.initialDependants = [...this.dependantIds];
        this.dependantsChanged.set(false);
        this.cdr.markForCheck();
      });
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  getBrokerAdminRmList(): void {
    this.loading.set(true);
    this.dataService
      .getRmList(this.organizationId, this.modalData.assignedRmUserId ?? '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ options, userMemberRole, rawList }) => {
        this.loading.set(false);
        this.userMemberRole = userMemberRole;
        this.rmOptions.set(options);
        this.filteredRmOptions.set([...options]);
        this.filteredData = this.sharedService.normaliseData(rawList, [
          NORMALISEDATA.MEMBER_FIRST_NAME,
          NORMALISEDATA.MEMBER_MIDDLE_NAME,
          NORMALISEDATA.MEMBER_LAST_NAME
        ]);
        this.cdr.markForCheck();
      });
  }

  getBrokerAdminClientList(): void {
    this.loading.set(true);
    this.dataService
      .getClientList(
        this.userId,
        this.currentRoleId,
        this.modalData.rmUserId ?? ''
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ options, rawList }) => {
        this.loading.set(false);
        const filteredOptions = options.filter(
          (opt) => opt.value !== this.modalData.clientUserId
        );
        this.clientOptions.set(filteredOptions);
        this.filteredClientOptions.set([...filteredOptions]);
        this.dependantOptions.set([...filteredOptions]);
        this.filteredData = this.sharedService.normaliseData(rawList, [
          'name',
          'pan'
        ]);
        const filteredForDependant = rawList.filter(
          (item: BrokerAdminRmClient) =>
            item.clientUserId !== this.modalData.clientUserId
        );
        this.filteredDataDependant = this.sharedService.normaliseData(
          filteredForDependant,
          ['name', 'pan']
        );
        if (
          this.modalData.actionButton === 'Update Dependants' &&
          this.dependantIds.length > 0
        ) {
          this.selectedDependants = [...this.dependantIds];
        }
        this.cdr.markForCheck();
      });
  }

  getClientRmInfo(clientUserId: string): void {
    this.showExistingRm.set(true);
    this.dataService
      .getClientRmInfo(clientUserId, this.currentRoleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: RMData | null) => {
        if (data) {
          this.existingRmDetails = data;
          const mappingUserId = this.existingRmDetails.rmUserId;
          if (mappingUserId !== null && mappingUserId !== '') {
            this.modalForm.value.oldMappingUserId = mappingUserId;
            this.valueRm = mappingUserId;
          } else {
            this.modalForm.value.oldMappingUserId = '';
          }
          this.cdr.markForCheck();
        }
      });
  }

  getModalData(): void {
    this.modalData = this.data?.modalData ?? this.modalData;
    if (!this.modalData) {
      return;
    }

    const action = this.modalData.actionButton;
    if (
      action === 'Assign RM' ||
      action === 'Reassign RM or Advisor' ||
      action === 'Reassign RM or Broker' ||
      action === 'Update Dependants'
    ) {
      this.clientName = `${this.modalData.clientFirstName ?? ''} ${this.modalData.clientMiddleName ?? ''} ${this.modalData.clientLastName ?? ''}`;
    } else if (action === 'Assign Client' || action === 'Reassign Client') {
      this.rmName =
        `${this.modalData.rmFirstName ?? ''} ${this.modalData.rmMiddleName ?? ''} ${this.modalData.rmLastName ?? ''}`
          .replace(/\s+/g, ' ')
          .trim();
    }
  }

  // ── Form validation helpers ───────────────────────────────────────────────

  filterNumericInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    this.modalForm.get('phoneNo')?.setValue(input.value, { emitEvent: false });
  }

  validatePhoneNumber(): void {
    const control = this.modalForm.get('phoneNo');
    const value: string = control?.value || '';
    if (!/^[6-9]/.test(value)) {
      control?.setErrors({ ...control.errors, startsWithInvalid: true });
      this.startsWithZero.set(true);
    } else if (control?.hasError('startsWithInvalid')) {
      const errors = { ...control.errors };
      delete errors['startsWithInvalid'];
      control.setErrors(Object.keys(errors).length ? errors : null);
      this.startsWithZero.set(false);
    }
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  onChangeChecked(
    event: CheckboxChangeEvent,
    modalData: ActionButton | undefined
  ): void {
    if (
      modalData?.actionButton === 'Assign Client' ||
      modalData?.actionButton === 'Reassign Client'
    ) {
      this.modalForm.value.oldMappingUserId = this.valueRm;
    }
  }

  onRmChange(event: { value: string }): void {
    if (event.value) {
      this.modalForm.controls['newMappingUserId'].setValue(event.value);
      this.cdr.markForCheck();
    }
  }

  onClientSelect(event: { value: string }): void {
    if (event?.value) {
      this.getClientRmInfo(event.value);
      this.cdr.markForCheck();
    }
  }

  onRmSelect(event: { value: string }): void {
    if (event?.value) {
      this.cdr.markForCheck();
    }
  }

  valueRmAssign(event: BrokerAdminRmClient): void {
    this.modalForm.controls['newMappingUserId'].setValue(event?.memberUserId);
  }

  valueFromGeneric(event: { userId: string }): void {
    this.modalForm.controls['clientUserId'].setValue(event?.userId);
    this.getClientRmInfo(event?.userId);
  }

  clientData(event: string[] | null | undefined): void {
    const updatedSelection = Array.isArray(event) ? [...event] : [];
    this.clientState.setSelectedClientIds(updatedSelection);
    this.isMultipleTrue.set(true);
    this.dependantsChanged.set(
      !this.areArraysEqual(updatedSelection, this.initialDependants)
    );
  }

  /** Kept for external/template compatibility. */
  filterOptions(): void {
    // Filtering is handled internally in getBrokerAdminClientList
  }

  /** Kept for external/template compatibility. */
  filterRmOptions(): void {
    // Filtering is handled internally in getBrokerAdminRmList
  }

  /** Delegates to the data service helper. */
  convertToCamcelCase(text: string): string {
    return this.dataService.toCamelCase(text);
  }

  // ── Dialog control ────────────────────────────────────────────────────────

  onClose(): void {
    if (this.dynamicDialogRef) {
      this.dynamicDialogRef.close(null);
    } else {
      this.closed.emit(null);
    }
  }

  onSubmit(): void {
    this.modalForm.markAllAsTouched();
    if (this.modalForm.invalid) {
      return;
    }

    const result: CloseEventResult = {
      resultForm: this.modalForm.value,
      flag: this.modalData.actionButton
    };

    if (this.isMultipleTrue()) {
      this.emitResult(result);
    } else {
      this.sharedService
        .createCommonInviteAssignReassign(result)
        .then(() => this.emitResult(result))
        .catch();
    }
  }

  private emitResult(result: CloseEventResult): void {
    if (this.dynamicDialogRef) {
      this.dynamicDialogRef.close(result);
    } else {
      this.closed.emit(result);
    }
  }

  private areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort((x, y) => x.localeCompare(y));
    const sortedB = [...b].sort((x, y) => x.localeCompare(y));
    return sortedA.every((v, i) => v === sortedB[i]);
  }
}
